import {
  Sparkles,
  BookOpen,
  Layers,
  ShieldCheck,
  AlertCircle,
  GraduationCap,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import type { AnnouncementCategory, AnnouncementSeverity, AnnouncementState } from './types'

/**
 * Category + severity catalog and per-category authoring templates.
 *
 * Single source of truth for how each update category is labelled, iconified,
 * and coloured across every reader surface (hub, timeline, bell, banner,
 * detail modal), AND the composer defaults + Markdown body skeletons.
 *
 * Icons follow DESIGN_SPEC.md; severity colours use the warm ramp from the
 * design tokens (critical/important/info). Templates map to the actual
 * t_anc_announcements columns (severity, mandatory_ack, realtime_email, weekly_digest,
 * source_citation, expires_at) + the t_anc_affected_codes child table.
 */

// ── Category metadata (reader surfaces) ──────────────────────────────────────

export interface CategoryMeta {
  label: string
  description: string
  Icon: LucideIcon
  /** Accent used for the category badge text/icon. */
  accent: string
  /** Soft background tint for the category badge. */
  accentSoft: string
}

export const CATEGORY_META: Record<AnnouncementCategory, CategoryMeta> = {
  product:     { label: 'Product',     description: 'Product releases, UI changes, bug fixes',              Icon: Sparkles,      accent: '#4338CA', accentSoft: '#EEEDFB' },
  guideline:   { label: 'Guideline',   description: 'Coding guidelines (CMS, AMA, AHA Coding Clinic)',       Icon: BookOpen,      accent: '#0E7C66', accentSoft: '#E3F1ED' },
  code_drop:   { label: 'Code drop',   description: 'Quarterly code drops (ICD-10, CPT, HCPCS, DRG)',        Icon: Layers,        accent: '#7C3AED', accentSoft: '#F1EAFD' },
  payer:       { label: 'Payer',       description: 'Payer policy (LCD/NCD, prior authorization)',           Icon: ShieldCheck,   accent: '#2563EB', accentSoft: '#E4EEFC' },
  compliance:  { label: 'Compliance',  description: 'Compliance & regulatory notices',                      Icon: AlertCircle,   accent: '#9E3B22', accentSoft: '#F9EAE4' },
  training:    { label: 'Training',    description: 'Training sessions and webinars',                        Icon: GraduationCap, accent: '#B45309', accentSoft: '#FBF0DD' },
  maintenance: { label: 'Maintenance', description: 'Scheduled maintenance and downtime',                    Icon: Wrench,        accent: '#454B57', accentSoft: '#EDEEF1' },
}

/** Tab / display order for categories. */
export const CATEGORY_ORDER: AnnouncementCategory[] = [
  'product', 'guideline', 'code_drop', 'payer', 'compliance', 'training', 'maintenance',
]

// ── Severity metadata ────────────────────────────────────────────────────────

export interface SeverityMeta {
  label: string
  color: string
  softBg: string
}

export const SEVERITY_META: Record<AnnouncementSeverity, SeverityMeta> = {
  critical:  { label: 'Critical',      color: '#9E3B22', softBg: '#F9EAE4' },
  important: { label: 'Important',     color: '#B45309', softBg: '#FBEEDD' },
  info:      { label: 'Informational', color: '#454B57', softBg: '#EDEEF1' },
}

export const SEVERITY_ORDER: AnnouncementSeverity[] = ['critical', 'important', 'info']

// ── State metadata (admin composer) ──────────────────────────────────────────

export const STATE_META: Record<AnnouncementState, { label: string; color: string; softBg: string }> = {
  draft:      { label: 'Draft',      color: '#454B57', softBg: '#EDEEF1' },
  published:  { label: 'Published',  color: '#0E7C66', softBg: '#E3F1ED' },
  archived:   { label: 'Archived',   color: '#8A8F99', softBg: '#EDEEF1' },
}

export const STATE_ORDER: AnnouncementState[] = ['draft', 'published', 'archived']

// ── Authoring templates (composer defaults + body skeleton) ──────────────────

export interface AnnouncementTemplate {
  defaultSeverity: AnnouncementSeverity
  defaultMandatoryAck: boolean
  defaultRealtimeEmail: boolean
  defaultWeeklyDigest: boolean
  /** Which optional fields are relevant/emphasised for this category. */
  fields: {
    sourceCitation: boolean
    affectedCodes: boolean
    expiresAt: boolean
  }
  /** Placeholder for the free-text expiry field, when relevant. */
  expiresLabel?: string
  /** Markdown skeleton pre-filled into the composer body. */
  bodyTemplate: string
}

export const ANNOUNCEMENT_TEMPLATES: Record<AnnouncementCategory, AnnouncementTemplate> = {
  product: {
    defaultSeverity: 'info',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: false,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: false, affectedCodes: false, expiresAt: false },
    bodyTemplate: [
      "## What's new",
      'Describe the feature, UI change, or bug fix.',
      '',
      '## Why it matters',
      'Who benefits and how their workflow improves.',
      '',
      '## How to use it',
      'Steps to try it, or a link to documentation.',
    ].join('\n'),
  },
  guideline: {
    defaultSeverity: 'important',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: true,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: true, affectedCodes: true, expiresAt: false },
    bodyTemplate: [
      '## Summary',
      'What changed in the coding guideline.',
      '',
      '## Effective date',
      'When this guidance takes effect.',
      '',
      '## What you need to do',
      'Concrete action items for coders.',
      '',
      '## Source',
      'Cite the transmittal / Coding Clinic reference (also fill the Source citation field).',
    ].join('\n'),
  },
  code_drop: {
    defaultSeverity: 'important',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: true,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: true, affectedCodes: true, expiresAt: false },
    bodyTemplate: [
      '## Overview',
      'Which code set(s) and version (ICD-10 / CPT / HCPCS / DRG).',
      '',
      '## New, revised & deleted codes',
      'Summarise the change; list the specific codes in the Affected codes field.',
      '',
      '## Effective date',
      '',
      '## Impact on coding',
      'What coders should watch for.',
    ].join('\n'),
  },
  payer: {
    defaultSeverity: 'important',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: true,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: true, affectedCodes: true, expiresAt: true },
    expiresLabel: 'Policy end date (optional)',
    bodyTemplate: [
      '## Payer & policy',
      'Which payer and policy (LCD/NCD id, prior-auth rule).',
      '',
      '## What changed',
      '',
      '## Effective date',
      '',
      '## Action required',
      'What coders/billers must do differently.',
    ].join('\n'),
  },
  compliance: {
    defaultSeverity: 'critical',
    defaultMandatoryAck: true,
    defaultRealtimeEmail: true,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: true, affectedCodes: false, expiresAt: false },
    bodyTemplate: [
      '## Notice',
      'The regulatory or compliance change.',
      '',
      '## Why this is critical',
      'Risk or obligation this addresses.',
      '',
      '## Required action',
      'Steps every user must take. This update requires acknowledgment.',
      '',
      '## Source / authority',
      'Cite the regulation or authority (also fill the Source citation field).',
    ].join('\n'),
  },
  training: {
    defaultSeverity: 'info',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: false,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: false, affectedCodes: false, expiresAt: true },
    expiresLabel: 'Registration closes (optional)',
    bodyTemplate: [
      '## Session',
      'Topic and presenter.',
      '',
      '## When',
      'Date & time (with timezone).',
      '',
      '## How to join',
      'Registration or meeting link.',
    ].join('\n'),
  },
  maintenance: {
    defaultSeverity: 'info',
    defaultMandatoryAck: false,
    defaultRealtimeEmail: true,
    defaultWeeklyDigest: true,
    fields: { sourceCitation: false, affectedCodes: false, expiresAt: true },
    expiresLabel: 'Maintenance ends',
    bodyTemplate: [
      '## Maintenance window',
      'Start – end (include timezone).',
      '',
      "## What's affected",
      'Which services or pages.',
      '',
      '## Expected impact',
      'Downtime, degraded performance, etc.',
      '',
      '## What to do',
      'Any action users should take before/after.',
    ].join('\n'),
  },
}
